﻿/**
 * FragmentMessageHandler
 * Routes incoming fragment messages to the correct manager.
 */

import type {
  FragmentMessage,
  FragmentUpdateMessagePayload,
  TransactionAssembleResponseMessagePayload,
  TransactionStatusMessagePayload,
  UpdatePaymentMethodMessagePayload,
} from '@anddone/ajs-util';
import type { Events } from './../Events';
import type { Orchestrator } from './../Orchestrator';
import type { StateManagement } from './../StateManagement';
import type { FragmentRegistry } from './../fragment-registry/FragmentRegistry';
import type { FragmentLifecycleManager } from './FragmentLifecycleManager';
import type { FragmentCommandManager } from './FragmentCommandManager';

import { createLogger } from '@anddone/ajs-logger';

const logger = createLogger('SDK', 'FragmentMessageHandler');

export class FragmentMessageHandler {
  /**
   * constructor
   *
   * @param dataState - Shared config and SDK state
   * @param orchestrator - Reference to orchestrator module
   * @param events - Reference to events module
   * @param fragmentRegistry - Reference to fragmentRegistry module
   * @param lifecycle - Fragment lifecycle manager
   * @param commands - Fragment commands manager
   */
  constructor(
    private readonly dataState: StateManagement,
    private readonly orchestrator: Orchestrator,
    private readonly events: Events,
    private readonly fragmentRegistry: FragmentRegistry,
    private readonly lifecycle: FragmentLifecycleManager,
    private readonly commands: FragmentCommandManager,
  ) {}

  /**
   * Handle incoming messages from fragment.
   *
   * These pass in from the OrchestratorMessageBus which sets up the postMessage listern.
   * This routes commands to the proper submodule for processing.
   *
   * @param message - Message received from fragment
   * @returns void
   */
  public handleMessage(message: FragmentMessage): void {
    const { type, fragmentName, data, fragmentId } = message;

    logger.debug('message', `Received type="${type}" from "${fragmentName ?? 'unknown'}"`, {
      type,
      fragmentName,
      fragmentId,
      data,
    });

    switch (type) {
      case 'handshake-request':
        this.lifecycle.handleHandshakeRequest(fragmentName!, fragmentId ?? '');
        this.commands.applyInitialValue(fragmentName!);
        break;

      case 'fragment-update':
        this.lifecycle.handleFragmentUpdate(fragmentName!, data as FragmentUpdateMessagePayload);
        break;

      case 'submit-transaction':
        this.commands.handleProcessRequest(fragmentName!);
        break;

      case 'transaction-assemble-response':
        this.commands.handleAssembleResponse(
          fragmentName!,
          data as TransactionAssembleResponseMessagePayload,
        );
        break;

      case 'transaction-status':
        this.commands.handleProcessResponse(data as TransactionStatusMessagePayload);
        break;

      case 'sibling-set-value':
        this.commands.setValues(data.targetFragment, data?.valuesToSet);
        break;

      case 'update-payment-method':
        this.dataState.paymentMethod.update(
          (data as UpdatePaymentMethodMessagePayload).paymentMethod,
        );
        break;

      default:
        logger.warn('message', `Unknown message type: "${type}"`, { type, fragmentName, data });
    }
  }
}
